import { apiAmplify, apiSrest } from '@/api';

import { isAmplifyError } from '@/api/apiAmplify/auth/util/amplifyError';

import { onBeforeMount, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCurrentUser } from '@/models/global/currentUser';
import {
  useCurrentOrganization,
  resolveOrganizationByCache,
} from '@/models/global/currentOrganization';
import { useLocalStorage } from '../utils/useLocalStorage';

// ログイン監視
export const useAuthentication = () => {
  const isLoadingAuthState = ref(true);
  const {
    currentUser,
    setCurrentUser,
    setOrganizations,
    setCognitoId,
    clearCurrentUser,
    updateIsAdmin,
    updateMFAState,
  } = useCurrentUser();
  const { removeLocalStorage, getLocalStorage } = useLocalStorage();
  const { currentOrganization, setCurrentOrganization } =
    useCurrentOrganization();

  // ログイン状態で、未ログイン専用ページを見ている場合は、`/`へリダイレクトする。
  // 未ログイン状態で、ログイン専用ページを見ている場合は、`/auth/sign_in`へリダイレクトする。
  const router = useRouter();

  // ログアウトさせる
  const forceSignOut = () => {
    const currentPath = location.pathname;
    clearCurrentUser();
    removeLocalStorage('App:CurrentUser:CognitoId');
    if (!unauthenticatedAllowed.includes(currentPath)) {
      isLoadingAuthState.value = true;
      setTimeout(() => {
        isLoadingAuthState.value = false;
      }, 200);
      router.push('/auth/sign_in');
    }
  };

  const checkAuthentication = async () => {
    // routerのcurrentRoute.value.pathはreactiveで遅延があるので、location.pathnameを使う。
    const currentPath = location.pathname;

    const user = await apiAmplify.auth.currentUser();
    if (user) {
      //
      // ログイン中の場合
      //
      setCurrentUser(user);

      // mfaStateが存在しない場合は取得して保存
      if (currentUser.value.mfaState === null) {
        try {
          const mfaState = await apiAmplify.auth.fetchMFAPreference();
          updateMFAState(mfaState);
        } catch (e) {
          if (isAmplifyError(e, 'NotAuthorizedException')) {
            // TODO: signOut用関数にまとめてDRYにする
            clearCurrentUser();
            removeLocalStorage('App:CurrentUser:CognitoId');
            if (!unauthenticatedAllowed.includes(currentPath)) {
              isLoadingAuthState.value = true;
              setTimeout(() => {
                isLoadingAuthState.value = false;
              }, 200);
              router.push('/auth/sign_in');
              return;
            }
          }
        }
      }

      // MFAが設定されていないユーザーは強制的にMFA設定画面に遷移する
      if ('/auth/setup_mfa' !== currentPath) {
        if (
          currentUser.value.mfaState === null ||
          currentUser.value.mfaState.preferred === undefined
        ) {
          router.push('/auth/setup_mfa');
          return;
        }
      }

      if (currentUser.value.organizations.length === 0) {
        const { organizations, cognitoId } = await apiSrest.user.appNames();
        setOrganizations(organizations);
        setCognitoId(cognitoId);
      }

      // 組織の確定と isAdmin / features の更新。
      // URL クエリを判定基準にする: グローバル ref は他コンポーネントが先に書き換える場合があり、
      // ref を見ると router.replace による URL 同期がスキップされるため。
      // updateIsAdmin を直接呼ぶ: SPA ナビゲーションでラベルが変わらないと watch が発火せず、
      // setCurrentUser で false にリセットされた isAdmin が復元されないため。
      const hasOrganizationQuery = Boolean(
        router.currentRoute.value.query.organization
      );

      if (!hasOrganizationQuery) {
        const cachedLabel = getLocalStorage('currentOrganization');
        const fallbackOrg = resolveOrganizationByCache(
          currentUser.value.organizations,
          cachedLabel
        );
        if (fallbackOrg?.label) {
          // URL に organization を付与し、次回リロード時に parseQueryFromWindow が読み込めるようにする
          await router.replace({
            query: {
              ...router.currentRoute.value.query,
              organization: fallbackOrg.label,
            },
          });
          setCurrentOrganization(fallbackOrg.label, fallbackOrg.tenantId);
          updateIsAdmin(fallbackOrg.label);
        } else if (currentOrganization.value.label) {
          updateIsAdmin(currentOrganization.value.label);
        }
      } else if (currentOrganization.value.label) {
        updateIsAdmin(currentOrganization.value.label);
      }

      if (unauthenticatedOnly.includes(currentPath)) {
        // 表示をブロックしてリダイレクトする。
        isLoadingAuthState.value = true;
        setTimeout(() => {
          // URL直接アクセス対策。URL直接アクセスした場合は、router.pushしてもrouterのwatchが走らないので、手動でLoadingを落とす。
          isLoadingAuthState.value = false;
        }, 200);
        router.push('/');
        return;
      }
    } else {
      //
      // 未ログインの場合
      //
      clearCurrentUser();
      removeLocalStorage('App:CurrentUser:CognitoId');
      if (!unauthenticatedAllowed.includes(currentPath)) {
        isLoadingAuthState.value = true;
        setTimeout(() => {
          isLoadingAuthState.value = false;
        }, 200);
        router.push('/auth/sign_in');
        return;
      }
    }
    isLoadingAuthState.value = false;
  };

  // タイミング: (1) 初回ページ読み込み。(2) HMR
  onBeforeMount(() => {
    checkAuthentication();
  });
  // タイミング: (1) RouterLinkでページ移動。(2) Root(/)以外での初回ページ読み込みのリダイレクト
  // 補足: 例えば/dashboardにURLで直接アクセスしたとき、currentRoute.value.pathは「/」になったあと「/dashboard」になる。そのため、routerのwatchが走る。
  watch(
    () => router.currentRoute.value.path,
    () => {
      // 初回ページ読み込みのリダイレクトでは実行しない
      if (isLoadingAuthState.value) {
        return;
      }
      checkAuthentication();
    }
  );

  watch(
    () => currentOrganization.value.label,
    (label) => {
      if (label) {
        updateIsAdmin(label);
      } else {
        currentUser.value.isAdmin = false;
        currentUser.value.canConfigureConnections = false;
        currentUser.value.features = [];
      }
    }
  );

  return { isLoadingAuthState };
};

// 未ログイン専用ページ (ログイン画面など)
const unauthenticatedOnly = ['/auth/sign_in', '/auth/sign_up'];

// 未ログイン許可ページ (ログイン画面や、規約など)
const unauthenticatedAllowed = [
  '/auth/sign_in',
  '/auth/sign_up',
  '/auth/forgot_password',
  '/auth/reset_password',
];
